import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getFileDownloadUrl } from "../../../../../api/firebase/firestoreService";
import {
  fetchSingleOrderDocs,
  resetDocuments,
} from "../../../../../redux/slice/nycbsPharmaExternal/nycbsDocumentsSlice";
import { AppDispatch, RootState } from "../../../../../redux/store/store";

interface UseClinicalDocumentProps {
  rowId?: string | number;
}

interface UseClinicalDocumentReturn {
  clinicalDocUrl: string;
  loading: boolean;
  isLoadingUrl: boolean;
  error: string;
}

export const useClinicalDocument = ({
  rowId,
}: UseClinicalDocumentProps): UseClinicalDocumentReturn => {
  const dispatch = useDispatch<AppDispatch>();
  const { data: singleOrderDocs, loading } = useSelector(
    (state: RootState) => state.nycbsDocuments,
  );
  const [clinicalDocUrl, setClinicalDocUrl] = useState<string>("");
  const [isLoadingUrl, setIsLoadingUrl] = useState<boolean>(false);
  const [error, setError] = useState<string>("");

  // Effect to fetch single order documents when rowId changes
  useEffect(() => {
    if (rowId) {
      dispatch(fetchSingleOrderDocs(String(rowId)));
    }

    return () => {
      dispatch(resetDocuments());
    };
  }, [rowId, dispatch]);

  // Effect to fetch clinical document URL when documents are received
  useEffect(() => {
    const fetchClinicalDocUrl = async () => {
      if (!singleOrderDocs || singleOrderDocs.length === 0) return;

      const clinicalDoc = singleOrderDocs.find(
        (doc) => doc.document_type === "clinical_attachment",
      );

      if (clinicalDoc?.file_path) {
        setIsLoadingUrl(true);
        setError("");
        try {
          const downloadUrl = await getFileDownloadUrl(clinicalDoc.file_path);
          if (downloadUrl) {
            setClinicalDocUrl(downloadUrl);
          } else {
            setError("Failed to load document URL");
          }
        } catch (err) {
          console.error("Error fetching clinical document URL:", err);
          setError("Error loading document");
        } finally {
          setIsLoadingUrl(false);
        }
      }
    };

    fetchClinicalDocUrl();
  }, [singleOrderDocs]);

  return {
    clinicalDocUrl,
    loading,
    isLoadingUrl,
    error,
  };
};
